import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import Breadcrumb from '../Component/Breadcrumb';
import Sidebar from '../../Dashboard/Component/Siderbar';
import Layout from '../../../Components/Layout';
import LoadingSpinner from '../../../Components/Common/LoadingSpinner';
import SearchAndFilter from '../../../Components/Common/SearchAndFilter';
import Pagination from '../../../Components/Common/Pagination';
import userService from '../../../services/userService';
import { OverlayTrigger, Tooltip } from "react-bootstrap";

export default function List() {
    const [deleteProcess, setDeleteProcess] = useState(0);
    const [deleteId, setDeleteId] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('');
    const perPage = 30;

    const navigate = useNavigate();
    const location = useLocation();
    const userData = JSON.parse(localStorage.getItem('user_data'));

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await userService.getUsers(userData.token, currentPage, perPage);
            setUsers(response.data);
        } catch (err) {
            setError(err.message);
            Swal.fire({
                position: "top-end",
                icon: "error",
                title: err.message,
                showConfirmButton: true,
            });
        } finally {
            setLoading(false);
        }
    }, [currentPage, userData.token]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers, location.state, deleteProcess]);

    const handleDeleteClick = (id) => {
        setDeleteId(id);
    };

    const confirmDelete = async () => {
        try {
            const response = await userService.deleteUser(userData.token, userData.user.agency_id, deleteId);
            
            if(response.status === 'success') {
                setDeleteProcess(prev => prev + 1);
                Swal.fire({
                    position: "top-end",
                    icon: "success",
                    title: "User deleted successfully",
                    showConfirmButton: false,
                    timer: 1500,
                });
            } else {
                throw new Error('Failed to delete the user');
            }
        } catch (error) {
            Swal.fire({
                position: "top-end",
                icon: "error",
                title: error.message,
                showConfirmButton: true,
            });
        } finally {
            setDeleteId(null);
        }
    };

    const handleUserEdit = useCallback((id) => {
        navigate(`/dashboard-user-edit/${id}`);
    }, [navigate]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    const handleSort = useCallback((sortValue) => {
        setSortBy(sortValue);
    }, []);

    const filteredAndSortedUsers = useMemo(() => {
        let result = [...users];

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(user => 
                user.name.toLowerCase().includes(searchLower) ||
                user.email.toLowerCase().includes(searchLower) ||
                user.phone.toLowerCase().includes(searchLower)
            );
        }

        // Sorting
        if (sortBy) {
            const [field, direction] = sortBy.split('_');
            result.sort((a, b) => {
                const compareResult = a[field].localeCompare(b[field]);
                return direction === 'asc' ? compareResult : -compareResult;
            });
        }

        return result;
    }, [users, searchTerm, sortBy]);

    if (loading) return <LoadingSpinner />;

    return (
        <Layout>
            <div className="modal fade" id="delete-list" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered modal-sm">
                    <div className="modal-content">
                        <div className="modal-body">
                            <div className="text-center">
                                <h5 className="mb-3">Delete User</h5>
                                <p className="mb-3">
                                    Are you sure you want to delete this user? This action cannot be undone.
                                </p>
                                <div className="d-flex align-items-center justify-content-center">
                                    <button className="btn btn-light me-2" data-bs-dismiss="modal">
                                        Cancel
                                    </button>
                                    <button
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                        data-bs-dismiss="modal"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Breadcrumb title="User List"/>

            <div className="content">
                <div className="container">
                    <div className="row">
                        <Sidebar>
                            <div className="row">
                                <div className="col-12">
                                    <div className="card booking-header border-0">
                                        <div className="card-body header-content d-flex align-items-center justify-content-between flex-wrap">
                                            <div>
                                                <h6 className="mb-1">Users</h6>
                                                <p className="fs-14 text-gray-6 fw-normal">Total: {filteredAndSortedUsers.length}</p>
                                            </div>

                                            <div className="d-flex align-items-center flex-wrap">
                                                <a href="/dashboard-user-add">
                                                    <button className='btn btn-primary'>+ Add User</button>
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card hotel-list" style={{ boxShadow:'0px 4px 24px 0px rgba(194, 194, 194, 0.25)' }}>
                                        <div className="card-body">
                                            <SearchAndFilter onSearch={handleSearch} onSort={handleSort} />

                                            {error && (
                                                <div className="alert alert-danger" role="alert">
                                                    {error}
                                                </div>
                                            )}

                                            <div className="custom-datatable-filter table-responsive">
                                                {filteredAndSortedUsers.length > 0 ? (
                                                    <>
                                                        <table className="datatable table">
                                                            <thead className="thead-light">
                                                                <tr>
                                                                    <th>ID</th>
                                                                    <th>Name Surname</th>
                                                                    <th>Username</th>
                                                                    <th>Email</th>
                                                                    <th>Phone</th>
                                                                    <th>Options</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filteredAndSortedUsers.map((item, index) => (
                                                                    <tr key={item.id} className="hover-row">
                                                                        <td className="text-center">{index + 1}</td>
                                                                        <td>
                                                                            <h6 className="fs-14 mb-1">{item.name} {item.surname}</h6>
                                                                        </td>
                                                                        <td>{item.username}</td>
                                                                        <td>{item.email}</td>
                                                                        <td>{item.phone}</td>
                                                                        <td>
                                                                            <div className="d-flex align-items-center">
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={<Tooltip>Show User</Tooltip>}
                                                                                >
                                                                                    <button 
                                                                                        onClick={() => handleUserEdit(item.id)}
                                                                                        className="btn me-2"
                                                                                >
                                                                                    <i className="isax isax-eye"></i>
                                                                                </button>
                                                                                </OverlayTrigger>
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={<Tooltip>Delete User</Tooltip>}
                                                                                >
                                                                                    <button
                                                                                        className="btn text-danger"
                                                                                        data-bs-toggle="modal"
                                                                                        data-bs-target="#delete-list"
                                                                                        onClick={() => handleDeleteClick(item.id)}
                                                                                >
                                                                                    <i className="isax isax-trash"></i>
                                                                                </button>
                                                                                </OverlayTrigger>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>

                                                        <Pagination
                                                            currentPage={currentPage}
                                                            totalPages={Math.ceil(filteredAndSortedUsers.length / perPage)}
                                                            onPageChange={setCurrentPage}
                                                        />
                                                    </>
                                                ) : (
                                                    <div className="alert alert-info text-center m-4">
                                                        No users found. {searchTerm && 'Try adjusting your search criteria.'}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Sidebar>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

// Add hover effect styles
const styles = `
.hover-row:hover {
    background-color: rgba(0, 0, 0, 0.05);
    transition: background-color 0.2s ease;
}
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

